<script setup lang="ts">
/* eslint-env vue/setup-compiler-macros */
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { Component } from 'vue';
import IconPayments from '@/components/icons/IconPayments.vue';
import PageView from '@/components/ui/PageView.vue';
import SectionTabView from '@/components/ui/SectionTabView.vue';
import AllPaymentsTab from '@/views/payments/AllPaymentsTab.vue';
import DepositsTab from '@/views/payments/DepositsTab.vue';
import OtherPaymentsTab from '@/views/payments/UpcomingPaymentsTab.vue';
// DPG-4532: Low Balance and the cancelled-only Other tab are commented out in
// LowBalancePaymentsTab.vue / OtherTransactionsTab.vue — the Other tab's status filter replaced them.
// import LowBalancePaymentsTab from '@/views/payments/LowBalancePaymentsTab.vue';
// import OtherTransactionsTab from '@/views/payments/OtherTransactionsTab.vue';
import type { PaymentsTabId } from '@/composables/usePayments';
import type { SectionTab } from '@/components/ui/SectionTabView.vue';

const TABS: SectionTab[] = [
    { id: 'all', label: 'All' },
    { id: 'deposits', label: 'Deposits' },
    { id: 'other', label: 'Other' },
    // { id: 'low-balance', label: 'Low Balance Payments' },
    // { id: 'other-transactions', label: 'Other Transactions' },
];

const DEFAULT_TAB_ID: PaymentsTabId = 'all';

const tabComponents: Record<PaymentsTabId, Component> = {
    all: AllPaymentsTab,
    deposits: DepositsTab,
    other: OtherPaymentsTab,
    // 'low-balance': LowBalancePaymentsTab,
    // 'other-transactions': OtherTransactionsTab,
};

const route = useRoute();
const router = useRouter();

const isPaymentsTabId = (value: unknown): value is PaymentsTabId => (
    typeof value === 'string'
    && TABS.some((tab) => tab.id === value)
);

const activeTabId = computed<PaymentsTabId>(() => {
    const tab = Array.isArray(route.query.tab)
        ? route.query.tab[0]
        : route.query.tab;

    return isPaymentsTabId(tab) ? tab : DEFAULT_TAB_ID;
});

const selectTab = (tabId: string) => {
    if (!isPaymentsTabId(tabId) || tabId === activeTabId.value) {
        return;
    }

    const nextQuery = { ...route.query };
    // A deep-linked status belongs to the tab it pointed at; the next tab starts from its own base.
    delete nextQuery.status;

    if (tabId === DEFAULT_TAB_ID) {
        delete nextQuery.tab;
    } else {
        nextQuery.tab = tabId;
    }

    router.push({
        name: 'payments',
        query: nextQuery,
    });
};
</script>

<template>
    <PageView title="Payments" :icon="IconPayments">
        <SectionTabView
            :tabs="TABS"
            :active-tab-id="activeTabId"
            @tab-change="selectTab"
        >
            <component
                :is="tabComponents[activeTabId]"
                :tab-id="activeTabId"
            />
        </SectionTabView>
    </PageView>
</template>
